/* eslint-disable ember/no-controller-access-in-routes, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';

export default class ReportBuilderEditRoute extends Route {
  controllerName = 'customise/report-builder';
  templateName = 'customise/report-builder';

  // Services
  @service
  router;

  @service
  store;

  // Parallel-fetch assessments and modules alongside the template so
  // setupController → setupForEdit can resolve module IDs to labels via
  // peekRecord on first render. Assessment/module fetches are catch'd so a
  // failure there degrades to "no labels" rather than killing the route.
  async model(params) {
    let [template] = await Promise.all([
      this.store.findRecord('reports/report-template', params.template_id, {
        reload: true,
        adapterOptions: { includeSections: true },
      }),
      this.store.findAll('assessments/assessment').catch(() => null),
      this.store.findAll('assessments/module').catch(() => null),
    ]);
    return template;
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let sections = this.store
      .peekAll('reports/report-template-section')
      .filter(
        (section) =>
          section.belongsTo('reportTemplateId').id() === model.get('id'),
      );
    controller.setupForEdit(model, sections);
  }

  resetController(controller) {
    super.resetController(...arguments);
    controller.cleanup();
  }

  activate() {
    super.activate(...arguments);
    if (this.onRouteWillChange) {
      this.router.off('routeWillChange', this.onRouteWillChange);
    }
    this.onRouteWillChange = (transition) => {
      // transition.abort() below fires a second routeWillChange for a
      // synthetic "settling back" transition with isAborted already true and
      // no targetName. Bail out here or the targetName check below never
      // matches it and we'd call abort() on it too, recursing forever.
      if (transition.isAborted) {
        return;
      }
      let controller = this.controller;
      // While a save is in flight, only allow same-route param changes
      // (e.g. the save's own replaceWith to a forked version's id). Any
      // transition off the report-builder-edit route is blocked until
      // the save finishes so its section-save loop can complete.
      if (controller.isSaving) {
        if (transition.targetName === this.routeName) return;
        if (controller.toaster) {
          controller.toaster.error(
            'Save in progress — please wait until it finishes before leaving.',
          );
        }
        transition.abort();
        return;
      }
      if (controller.isDirty) {
        if (
          !window.confirm(
            'You have unsaved changes. Are you sure you want to leave?',
          )
        ) {
          transition.abort();
        }
      }
    };
    this.router.on('routeWillChange', this.onRouteWillChange);
  }

  deactivate() {
    super.deactivate(...arguments);
    if (this.onRouteWillChange) {
      this.router.off('routeWillChange', this.onRouteWillChange);
      this.onRouteWillChange = null;
    }
  }
}
